import { useEffect, useState } from 'react';
import * as XLSX from 'xlsx';
import { Button } from '@/client/components/ui/button';
import { Checkbox } from '@/client/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/client/components/ui/dialog';
import { Label } from '@/client/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/client/components/ui/select';
import { trpc } from '@/client/utils/trpc';

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

interface ExportDialogProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  defaultYear: number;
  defaultMonth: number;
}

export function ExportDialog({ open, onOpenChange, defaultYear, defaultMonth }: ExportDialogProps) {
  const [expYear, setExpYear] = useState(defaultYear);
  const [expMonth, setExpMonth] = useState(defaultMonth);
  const [projectId, setProjectId] = useState<string>('');
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  const [allUsers, setAllUsers] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const { data: projects = [] } = trpc.projects.list.useQuery();
  const { data: employees = [] } = trpc.employees.list.useQuery();

  const exportQuery = trpc.timesheets.adminExport.useQuery(
    {
      year: expYear,
      month: expMonth,
      project_id: projectId || undefined,
      user_ids: allUsers ? undefined : Array.from(selectedUserIds),
    },
    { enabled: false },
  );

  useEffect(() => {
    if (open) {
      setExpYear(defaultYear);
      setExpMonth(defaultMonth);
      setProjectId('');
      setSelectedUserIds(new Set());
      setAllUsers(true);
      setExportError(null);
    }
  }, [open, defaultYear, defaultMonth]);

  function toggleUser(id: string) {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function handleExport() {
    setExporting(true);
    setExportError(null);
    try {
      const { data, error } = await exportQuery.refetch();
      if (error) {
        setExportError(error.message);
        return;
      }
      if (!data || data.length === 0) {
        setExportError('No entries found for the selected filters.');
        return;
      }

      const rows = data.map((r) => ({
        Employee: r.employee,
        Project: r.project,
        Date: r.date,
        Description: r.description,
        Hours: r.hours,
      }));

      const ws = XLSX.utils.json_to_sheet(rows);
      ws['!cols'] = [{ wch: 24 }, { wch: 24 }, { wch: 12 }, { wch: 40 }, { wch: 8 }];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Timesheets');

      const monthLabel = MONTHS[expMonth - 1];
      XLSX.writeFile(
        wb,
        `timesheets-${expYear}-${String(expMonth).padStart(2, '0')}-${monthLabel}.xlsx`,
      );

      onOpenChange(false);
    } catch (err) {
      setExportError(err instanceof Error ? err.message : 'Export failed.');
    } finally {
      setExporting(false);
    }
  }

  const yearOptions = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Export Timesheets</DialogTitle>
        </DialogHeader>
        <div className="space-y-5">
          <div className="flex gap-3">
            <div className="flex-1 space-y-1.5">
              <Label>Year</Label>
              <Select value={String(expYear)} onValueChange={(v) => setExpYear(Number(v))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {yearOptions.map((y) => (
                    <SelectItem key={y} value={String(y)}>
                      {y}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 space-y-1.5">
              <Label>Month</Label>
              <Select value={String(expMonth)} onValueChange={(v) => setExpMonth(Number(v))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MONTHS.map((name, i) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: i+1 is the stable month number (1-12)
                    <SelectItem key={i + 1} value={String(i + 1)}>
                      {name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Project</Label>
            <Select
              value={projectId || '_all'}
              onValueChange={(v) => setProjectId(v === '_all' ? '' : v)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_all">All projects</SelectItem>
                {projects.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Employees</Label>
              <button
                type="button"
                className="text-xs text-muted-foreground hover:underline"
                onClick={() => {
                  setAllUsers((v) => {
                    if (!v) setSelectedUserIds(new Set());
                    return !v;
                  });
                }}
              >
                {allUsers ? 'Select specific' : 'All employees'}
              </button>
            </div>
            {!allUsers && (
              <div className="max-h-40 overflow-y-auto rounded-md border p-2 space-y-2">
                {employees.map((emp) => (
                  <label
                    key={emp.id}
                    htmlFor={`emp-${emp.id}`}
                    className="flex items-center gap-2 cursor-pointer text-sm"
                  >
                    <Checkbox
                      id={`emp-${emp.id}`}
                      checked={selectedUserIds.has(emp.id)}
                      onCheckedChange={() => toggleUser(emp.id)}
                    />
                    <span>{emp.full_name || emp.email}</span>
                  </label>
                ))}
              </div>
            )}
            {allUsers && (
              <p className="text-sm text-muted-foreground">All employees will be included.</p>
            )}
          </div>

          {exportError && <p className="text-sm text-destructive">{exportError}</p>}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleExport}
              disabled={exporting || (!allUsers && selectedUserIds.size === 0)}
            >
              {exporting ? 'Exporting…' : 'Export XLSX'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
